import { Button } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { GetCreditNotesResponse } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { formatPrice } from '@vy/services';
import type { Currency, IntegrationProviders } from '@vy/types';
import { isNotNullOrUndefined, vyDate } from '@vy/utils';
import { formatDate } from '../../../../utils/date';
import { CreditNoteTableDotMenu } from './CreditNoteTableDotMenu';

type CreditNote = GetCreditNotesResponse['creditNotes'][number];

export const CREDIT_NOTES_COLUMNS_KEYS = Object.freeze({
  DATE: 'date',
  AMOUNT: 'amount',
  ORIGINAL_INVOICE: 'invoiceNumber',
  VIEW: 'view',
  DOTS_MENU: 'dotsMenu',
});

type GetCreditNotesColumnsOptions = {
  onViewPdf: (creditLedgerId: string) => void;
  customerId: string;
  currency: Currency;
  connectedIntegrations: IntegrationProviders[];
  customerName: string;
  contactEmails: string[];
};

export function getCreditNotesTableColumns({
  onViewPdf,
  customerId,
  currency,
  connectedIntegrations,
  customerName,
  contactEmails,
}: GetCreditNotesColumnsOptions): GridColDef<CreditNote>[] {
  return [
    {
      field: CREDIT_NOTES_COLUMNS_KEYS.DATE,
      sortable: false,
      headerName: 'Date Created',
      flex: 1,
      valueGetter: (_value, row) => formatDate(vyDate(row.date)),
    },
    {
      field: CREDIT_NOTES_COLUMNS_KEYS.AMOUNT,
      sortable: false,
      headerName: 'Amount',
      flex: 1,
      valueGetter: (_value, row) => formatPrice(row.amount, currency),
    },
    {
      field: CREDIT_NOTES_COLUMNS_KEYS.ORIGINAL_INVOICE,
      sortable: false,
      headerName: 'Original Invoice',
      flex: 1,
      renderCell: ({ row }) => {
        if (isNotNullOrUndefined(row.invoiceId)) {
          return (
            <Button
              variant="text"
              size="small"
              href={`/invoices/${row.invoiceId}/customer/${customerId}/dynamic`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                textTransform: 'none',
                padding: 0,
                minWidth: 'auto',
                '&:hover': { textDecoration: 'underline', background: 'none' },
              }}
            >
              {row.invoiceNumber}
            </Button>
          );
        }
        return row.invoiceNumber || '—';
      },
    },
    {
      field: CREDIT_NOTES_COLUMNS_KEYS.VIEW,
      headerName: '',
      width: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onViewPdf(row.id);
          }}
          sx={{
            display: 'flex',
            alignItems: 'space-between',
            gap: '2px',
            padding: '6px 12px',
          }}
        >
          View
          <VyIcon icon="ArrowSmallRight" />
        </Button>
      ),
    },
    {
      field: CREDIT_NOTES_COLUMNS_KEYS.DOTS_MENU,
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: ({ row }) => (
        <CreditNoteTableDotMenu
          creditLedgerId={row.id}
          customerId={row.customerId}
          erpId={row.erpId}
          syncedIntegrations={row.syncedIntegrations}
          connectedIntegrations={connectedIntegrations}
          customerName={customerName}
          contactEmails={contactEmails}
        />
      ),
    },
  ];
}
